"use client";

import { useEffect } from "react";

/**
 * Global in-page anchor handler. Intercepts every click on an `a[href^="#"]`
 * and smooth-scrolls to the matching element instead of letting the browser
 * navigate. This matters in two places:
 *
 * 1. Sandboxed previews (about:srcdoc) — native hash navigation there either
 *    does nothing or reloads the document, which is exactly the "nav links
 *    don't work / Contact reloads the page" bug this fixes.
 * 2. Consistency — one handler covers the header nav, mobile nav, footer nav
 *    and every CTA button without per-link wiring.
 *
 * Honors prefers-reduced-motion by falling back to an instant jump.
 */
export function SmoothScroll() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      // Respect modified clicks (open in new tab etc.).
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as Element).closest?.('a[href^="#"]');
      if (!anchor) return;

      const id = anchor.getAttribute("href")!.slice(1);
      const el = id ? document.getElementById(id) : null;
      if (!el) return;

      e.preventDefault();
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });

      // Keep the hash in the URL for deep-linking; sandboxed documents throw
      // on history writes, so failure is acceptable and silent.
      try {
        history.replaceState(null, "", `#${id}`);
      } catch {
        /* about:srcdoc — no history access */
      }
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
